import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { platformSettings } from '@/api/platformSettings';
import type { AwsPollyView } from '@/api/platformSettings';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertCircle } from 'lucide-react';

export function AwsPollySettings() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState<AwsPollyView | null>(null);
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    platformSettings.getAwsPolly().then((v) => {
      if (ignore) return;
      setCurrent(v);
      if (v.region) setRegion(v.region);
    }).catch(() => { /* leave defaults */ });
    return () => { ignore = true; };
  }, []);

  async function save() {
    setStatus(null);
    setSaving(true);
    try {
      await platformSettings.putAwsPolly({
        access_key_id: accessKeyId.trim(),
        secret_access_key: secretAccessKey,
        region: region.trim(),
      });
      setStatus(t('awsPolly.saved'));
      setAccessKeyId(''); setSecretAccessKey('');
      const next = await platformSettings.getAwsPolly();
      setCurrent(next);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : t('common.failedToSave'));
    } finally {
      setSaving(false);
    }
  }

  async function clear() {
    if (!confirm(t('awsPolly.clearConfirmMessage'))) return;
    setStatus(null); setSaving(true);
    try {
      await platformSettings.putAwsPolly({ clear: true });
      setStatus(t('awsPolly.credentialsCleared'));
      const next = await platformSettings.getAwsPolly();
      setCurrent(next);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Clear failed.');
    } finally { setSaving(false); }
  }

  return (
    <>
      <PageHeader
        eyebrow={t('navGroups.settings')}
        title={t('awsPolly.title')}
        description={t('awsPolly.description')}
      />
      <div className="max-w-2xl space-y-6">
        <Card className={current?.configured ? 'border-emerald-200/60' : 'border-amber-200/60'}>
          <CardContent className="py-4">
            {current?.configured ? (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-foreground">{t('awsPolly.configuredStatus')}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                    Access key: {current.access_key_id_redacted} &nbsp;·&nbsp; Region: {current.region}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-foreground">{t('awsPolly.notConfiguredStatus')}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {t('awsPolly.notConfiguredHint')}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('awsPolly.awsCredentials')}</CardTitle>
            <CardDescription>
              {t('awsPolly.credentialsDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="awsAkid">{t('awsPolly.accessKeyId')}</Label>
              <Input
                id="awsAkid" value={accessKeyId}
                onChange={(e) => setAccessKeyId(e.target.value)}
                placeholder={current?.access_key_id_redacted ?? 'AKIA...'}
                autoComplete="off" spellCheck={false}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="awsSecret">{t('awsPolly.secretAccessKey')}</Label>
              <Input
                id="awsSecret" type="password" value={secretAccessKey}
                onChange={(e) => setSecretAccessKey(e.target.value)}
                placeholder={current?.configured ? '••••••••••••••••••••••••••••••••' : ''}
                autoComplete="off" spellCheck={false}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="awsRegion">{t('awsPolly.region')}</Label>
              <Input
                id="awsRegion" value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="us-east-1"
              />
              <p className="text-xs text-muted-foreground">
                {t('awsPolly.regionHint')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={save} disabled={saving || !accessKeyId || !secretAccessKey}>
                {saving ? t('common.saving') : t('awsPolly.saveCredentials')}
              </Button>
              {current?.configured && (
                <Button variant="outline" onClick={clear} disabled={saving}>
                  {t('awsPolly.clearCredentials')}
                </Button>
              )}
            </div>
            {status && <p role="alert" className="text-sm">{status}</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('awsPolly.howToGetCredentials')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-600">
            <ol className="list-decimal ps-5 space-y-2">
              <li>Sign in to the AWS Console and open <strong>IAM</strong>.</li>
              <li>Create a new IAM user (e.g. <code className="rounded bg-slate-100 px-1">ivrello-polly</code>) with
                <strong> programmatic access</strong> only.</li>
              <li>
                Attach this inline policy — it gives access to Polly only, nothing else in your AWS account:
                <pre className="mt-2 rounded bg-slate-900 text-slate-50 p-3 text-[11px] overflow-x-auto font-mono">{`{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Action": [
      "polly:SynthesizeSpeech",
      "polly:DescribeVoices"
    ],
    "Resource": "*"
  }]
}`}</pre>
              </li>
              <li>Complete the wizard. AWS shows the access key ID and secret access key
                <strong> once</strong> — copy them now; you can&rsquo;t view the secret again.</li>
              <li>Paste into the form above and save. TTS will be live within 60 seconds (local cache invalidation).</li>
            </ol>
            <p className="pt-2 border-t border-border text-xs">
              Polly pricing (standard voice): roughly $4 per 1 million characters.
              Ivrello caches synthesis output by text+language+voice so repeat prompts don&rsquo;t re-hit the API.
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
